/**
 * The Color — Theme JavaScript
 * Main JS: scroll lock, focus trap, mobile menu, header scroll behaviour,
 * announcement bar dismiss, dropdown navigation, smooth scroll, overlay.
 * Vanilla ES modules — no frameworks, no jQuery.
 *
 * Shared contract (B15, 2026-10-02) — used by cart.js, card.js, product.js,
 * search.js, the filter drawer and every other dialog:
 *   TheColor.ScrollLock.lock(owner) / .unlock(owner) / .isLocked()
 *     Ref-counted: the page unlocks only when every owner has let go, and
 *     the scroll position comes back on the same frame (no smooth-scroll
 *     replay from the top). Locks with body{position:fixed} (base.css
 *     body.drawer-open), the one technique iOS 15 and older respect —
 *     body{overflow:hidden} alone never locked this page because base.css
 *     sets html{overflow-x:hidden}.
 *   TheColor.trapFocus(element, { focusFirst }) → release()
 *     Tab / Shift+Tab wrap at the dialog's last / first Tab stop, and
 *     whenever focus is outside the dialog or on the dialog itself (Shift+Tab
 *     from the dialog used to escape to the page). Collapsed content
 *     (visibility:hidden) is skipped. Nested traps: the newest wins.
 *     Iframes and shadow-DOM hosts (the drawer's express wallets) stay in
 *     the Tab cycle: an end guard wraps out of them (QA Q3-02).
 *   --tc-fixed-top (CSS custom property on <html>): the fixed chrome's
 *     height. The sticky header script (sections/header.liquid) publishes
 *     it; with no sticky header this file sets 0px, so base.css's
 *     scroll-padding-top never reserves room for a header that scrolls away.
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     1. DOM Cache & Utilities
     -------------------------------------------------------------------------- */

  const selectors = {
    header: '.tc-header',
    headerSticky: '.tc-header--sticky',
    overlay: '#tc-overlay',
    mobileNav: '[data-mobile-nav]',
    mobileNavOpen: '[data-action="open-mobile-nav"]',
    mobileNavClose: '[data-action="close-mobile-nav"]',
    mobileNavToggle: '.tc-mobile-nav__toggle',
    announcementDismiss: '[data-dismiss="announcement"]',
    dropdownTrigger: '[data-dropdown] button[aria-haspopup]',
    dropdownParent: '[data-dropdown]',
  };

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* B2-26: in-page scrolls jump instead of glide when motion is reduced. */
  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  /* --------------------------------------------------------------------------
     2. Scroll lock (ref-counted)
     -------------------------------------------------------------------------- */

  const ScrollLock = {
    _owners: [],
    _y: 0,

    isLocked() {
      return this._owners.length > 0;
    },

    lock(owner) {
      const key = owner || 'default';
      if (this._owners.indexOf(key) !== -1) return;
      this._owners.push(key);
      if (this._owners.length > 1) return;

      const root = document.documentElement;
      const body = document.body;
      this._y = window.pageYOffset || root.scrollTop || 0;
      root.classList.add('tc-scroll-locked');
      body.classList.add('drawer-open');
      body.style.top = `-${this._y}px`;
      document.dispatchEvent(new CustomEvent('tc:scrolllock', { detail: { locked: true } }));
    },

    unlock(owner) {
      const key = owner || 'default';
      const i = this._owners.indexOf(key);
      if (i === -1) return;
      this._owners.splice(i, 1);
      if (this._owners.length) return;

      const root = document.documentElement;
      const body = document.body;
      body.classList.remove('drawer-open');
      body.style.top = '';
      root.classList.remove('tc-scroll-locked');
      /* html{scroll-behavior:smooth} turned every restore into a glide from
         the top of the page back down (0 → 1210 → 1500 over 1.5s). */
      const prev = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, this._y);
      root.style.scrollBehavior = prev;
      document.dispatchEvent(new CustomEvent('tc:scrolllock', { detail: { locked: false } }));
    },
  };

  /* --------------------------------------------------------------------------
     3. Focus trap
     -------------------------------------------------------------------------- */

  /* Controls the trap can list and focus itself. Iframes and custom
     elements are handled separately (see "opaque" Tab stops below). */
  const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const trapStack = [];

  function isVisible(el) {
    if (el.checkVisibility) {
      /* visibilityProperty: collapsed accordions (visibility:hidden) are not
         Tab stops; checkVisibilityCSS is the older name for the same flag. */
      return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
    }
    const cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    return el.offsetParent !== null || cs.position === 'fixed';
  }

  /* b comes after a in Tab order (a's own descendants included). */
  function isAfter(a, b) {
    return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  /* b comes before a in Tab order (a's ancestors included). */
  function isBefore(a, b) {
    return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING);
  }

  /**
   * Trap focus within an element. Returns a release function.
   *
   * "Opaque" Tab stops (QA Q3-02, 2026-10-03): the cart drawer's express
   * wallets are custom elements whose buttons sit in closed shadow roots
   * (Shop Pay, Google Pay) or in a cross-origin iframe (PayPal). While one
   * has focus, document.activeElement is the host / the iframe, never a
   * listed control, and inside the iframe this page sees no keydown at all.
   * So the trap never wraps out of them by itself: the browser moves through
   * them, and an invisible end guard (the dialog's last child, only while
   * such stops exist) sends focus back to the first control when Tab leaves
   * the last one. Shift+Tab from the first control parks focus on that guard
   * and lets the browser's own Shift+Tab step back from it, so the wrap lands
   * on the real last stop (a wallet on phones, where "View full cart" is
   * hidden) instead of skipping it.
   */
  function trapFocus(element, options) {
    if (!element) return function noop() {};
    const opts = options || {};
    let guard = null;
    let guardPass = false;

    // Re-queried on every Tab so late-fetched content (upsell cards, rebuilt
    // rows) is included and hidden controls never become wrap boundaries.
    // tabIndex < 0 (e.g. the aria-hidden image links in cart rows and cards)
    // is never a Tab stop, so it can't be a wrap boundary or the first focus.
    function getFocusable() {
      return Array.from(element.querySelectorAll(FOCUSABLE)).filter((el) =>
        el !== guard && el.tabIndex >= 0 && !el.closest('[inert]') && isVisible(el)
      );
    }

    function getOpaque() {
      const out = [];
      const all = element.getElementsByTagName('*');
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        if (el === guard) continue;
        if (el.tagName !== 'IFRAME' && el.tagName.indexOf('-') === -1) continue;
        if (el.getAttribute('tabindex') === '-1') continue;
        if (el.closest('[inert]') || !isVisible(el)) continue;
        out.push(el);
      }
      return out;
    }

    function onGuardFocus() {
      if (guardPass || released) return;
      if (trapStack[trapStack.length - 1] !== entry) return;
      const first = getFocusable()[0];
      if (first) first.focus();
    }

    function ensureGuard() {
      if (!guard) {
        guard = document.createElement('span');
        guard.tabIndex = 0;
        guard.setAttribute('data-tc-focus-guard', '');
        guard.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;margin:0;padding:0;border:0;overflow:hidden;opacity:0;pointer-events:none;outline:none';
        guard.addEventListener('focus', onGuardFocus);
      }
      if (element.lastElementChild !== guard) element.appendChild(guard);
    }

    // Wallets mount after the drawer opens (and a click can put focus in
    // one): place the guard as soon as one appears.
    function onFocusIn() {
      if (!guard && getOpaque().length) ensureGuard();
    }

    const entry = { element };
    let released = false;

    function wrapToEnd(e, last, tail) {
      if (tail) {
        // Something after `last` is a Tab stop we cannot focus directly:
        // stand on the end guard and let this Shift+Tab continue from it.
        ensureGuard();
        guardPass = true;
        guard.focus({ preventScroll: true });
        guardPass = false;
        return;
      }
      e.preventDefault();
      last.focus();
    }

    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (trapStack[trapStack.length - 1] !== entry) return; // newest trap only

      const focusable = getFocusable();
      if (!focusable.length) {
        // The listener is on the document: a caller that closed its dialog
        // without releasing must not swallow every Tab on the page. A trap
        // whose element is gone or hidden lets go of itself.
        if (!element.isConnected || !isVisible(element)) {
          releaseFocus();
          return;
        }
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const opaque = getOpaque();
      if (opaque.length) ensureGuard();
      const active = document.activeElement;
      const inside = !!active && active !== element && active !== guard && element.contains(active);
      // Wrap whenever focus is not inside the dialog (the dialog itself, the
      // page behind, <body> after a row was removed).
      if (!inside) {
        if (e.shiftKey) {
          wrapToEnd(e, last, opaque.some((el) => isAfter(last, el)));
        } else {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      // On (or in) a wallet: let the browser move on; the guard catches the
      // way out at the end.
      if (opaque.some((el) => el === active || el.contains(active))) return;

      if (e.shiftKey) {
        const atStart = !focusable.some((el) => el !== active && isBefore(active, el)) &&
          !opaque.some((el) => isBefore(active, el) && !el.contains(active));
        if (atStart) wrapToEnd(e, last, opaque.some((el) => isAfter(last, el)));
        return;
      }
      const atEnd = !focusable.some((el) => el !== active && isAfter(active, el)) &&
        !opaque.some((el) => isAfter(active, el));
      if (atEnd) {
        e.preventDefault();
        first.focus();
      }
    }

    entry.handler = handleTab;
    trapStack.push(entry);
    document.addEventListener('keydown', handleTab);
    element.addEventListener('focusin', onFocusIn);
    if (getOpaque().length) ensureGuard();

    function releaseFocus() {
      if (released) return;
      released = true;
      document.removeEventListener('keydown', handleTab);
      element.removeEventListener('focusin', onFocusIn);
      if (guard) {
        guard.removeEventListener('focus', onGuardFocus);
        if (guard.parentNode) guard.parentNode.removeChild(guard);
        guard = null;
      }
      const i = trapStack.indexOf(entry);
      if (i !== -1) trapStack.splice(i, 1);
    }

    if (opts.focusFirst !== false) {
      const first = getFocusable()[0];
      if (first) first.focus();
    }

    return releaseFocus;
  }

  /* --------------------------------------------------------------------------
     4. Overlay
     -------------------------------------------------------------------------- */

  const Overlay = {
    el: null,
    _shown: false,

    init() {
      this.el = document.querySelector(selectors.overlay);
      if (!this.el) return;

      this.el.addEventListener('click', () => {
        this.hide();
        MobileNav.close();
        // Cart drawer close is handled in cart.js
        document.dispatchEvent(new CustomEvent('overlay:clicked'));
      });
    },

    show() {
      if (!this.el) this.el = document.querySelector(selectors.overlay);
      if (!this.el) return;
      this.el.classList.add('is-active');
      if (this._shown) return;
      this._shown = true;
      ScrollLock.lock('overlay');
    },

    hide() {
      if (!this.el) return;
      this.el.classList.remove('is-active');
      // An overlay click calls hide() up to three times (overlay, menu, cart):
      // only the one that follows a real show() restores the scroll position.
      if (!this._shown) return;
      this._shown = false;
      ScrollLock.unlock('overlay');
    },
  };

  /* --------------------------------------------------------------------------
     5. Mobile Navigation
     -------------------------------------------------------------------------- */

  const MobileNav = {
    drawer: null,
    openBtn: null,
    closeBtn: null,
    header: null,
    releaseFocus: null,
    previousFocus: null,
    _unliftTimer: null,

    init() {
      this.drawer = document.querySelector(selectors.mobileNav);
      this.openBtn = document.querySelector(selectors.mobileNavOpen);
      this.closeBtn = document.querySelector(selectors.mobileNavClose);
      this.header = this.openBtn ? this.openBtn.closest(selectors.header) : null;

      if (!this.drawer || !this.openBtn) return;

      this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Submenu toggles (B2-12): animate to the panel's REAL height. The CSS
      // ceiling (1400px) made opening snap and closing stall ~140ms while the
      // max-height ran down through empty space. Open panels settle on
      // max-height:none so a rotation can't clip them.
      const toggles = this.drawer.querySelectorAll(selectors.mobileNavToggle);
      toggles.forEach((toggle) => {
        const sub = toggle.nextElementSibling;
        if (sub && toggle.getAttribute('aria-expanded') === 'true') {
          sub.style.maxHeight = 'none';
        }
        if (sub) {
          sub.addEventListener('transitionend', (e) => {
            if (e.target !== sub || e.propertyName !== 'max-height') return;
            if (toggle.getAttribute('aria-expanded') === 'true') sub.style.maxHeight = 'none';
          });
        }
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          if (sub) {
            sub.style.maxHeight = `${sub.scrollHeight}px`;
            if (isExpanded) {
              void sub.offsetHeight; // commit the start height, then collapse
              sub.style.maxHeight = '0px';
            }
          }
          toggle.setAttribute('aria-expanded', String(!isExpanded));
        });
      });

      // Close drawer when clicking on navigation links (but not toggle buttons)
      const navLinks = this.drawer.querySelectorAll('.tc-mobile-nav__link:not(.tc-mobile-nav__toggle), .tc-mobile-nav__sublink, .tc-mobile-nav__pill, .tc-mobile-nav__account');
      navLinks.forEach((link) => {
        link.addEventListener('click', () => {
          // Delay close slightly to allow navigation to start
          setTimeout(() => this.close(), 150);
        });
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    },

    open() {
      if (!this.drawer || this.drawer.classList.contains('is-open')) return;
      clearTimeout(this._unliftTimer);
      this.previousFocus = document.activeElement;
      // The header's inert observer only re-enables the drawer in a microtask;
      // lift inert now so trapFocus below can move focus into the drawer.
      this.drawer.removeAttribute('inert');
      this.drawer.classList.add('is-open');
      if (this.header) this.header.classList.add('is-mobile-nav-open');
      this.openBtn.setAttribute('aria-expanded', 'true');
      Overlay.show();
      this.releaseFocus = trapFocus(this.drawer);
    },

    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.openBtn.setAttribute('aria-expanded', 'false');
      // B2-11: the header stays lifted (z 310) until the slide-out ends, so
      // the panel never drops under the overlay mid-animation.
      clearTimeout(this._unliftTimer);
      this._unliftTimer = setTimeout(() => {
        if (this.header && !this.drawer.classList.contains('is-open')) {
          this.header.classList.remove('is-mobile-nav-open');
        }
      }, 300);
      Overlay.hide();
      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      if (this.previousFocus && document.contains(this.previousFocus)) {
        this.previousFocus.focus({ preventScroll: true });
      }
    },
  };

  /* --------------------------------------------------------------------------
     6. Header Scroll Behaviour (shadow once scrolled)
     -------------------------------------------------------------------------- */

  const HeaderScroll = {
    header: null,
    scrollThreshold: 50,

    init() {
      this.header = document.querySelector(selectors.header);
      if (!this.header) return;

      let ticking = false;

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.update();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this.update();
    },

    update() {
      // While a dialog holds the page, scrollY reads 0: keep the last state.
      if (!this.header || ScrollLock.isLocked()) return;
      const scrolled = window.scrollY > this.scrollThreshold;
      this.header.classList.toggle('is-scrolled', scrolled);
    },
  };

  /* --------------------------------------------------------------------------
     7. Announcement Bar Dismiss
     -------------------------------------------------------------------------- */

  const AnnouncementBar = {
    init() {
      const dismissBtns = document.querySelectorAll(selectors.announcementDismiss);
      const root = document.documentElement;
      let hasVisibleBar = false;

      dismissBtns.forEach((btn) => {
        const bar = btn.closest('.tc-announcement');
        if (!bar) return;

        hasVisibleBar = true;

        btn.addEventListener('click', () => {
          bar.classList.add('is-dismissed');
          // Keep sticky header flush to top once all announcement bars are dismissed.
          const hasRemainingVisible = document.querySelector('.tc-announcement:not(.is-dismissed)');
          root.style.setProperty('--announcement-offset', hasRemainingVisible ? 'var(--announcement-height)' : '0px');
        });
      });

      root.style.setProperty('--announcement-offset', hasVisibleBar ? 'var(--announcement-height)' : '0px');
    },
  };

  /* --------------------------------------------------------------------------
     8. Desktop Dropdown Navigation (legacy [data-dropdown] markup; the header
        itself uses its own disclosure script)
     -------------------------------------------------------------------------- */

  const DropdownNav = {
    init() {
      const triggers = document.querySelectorAll(selectors.dropdownTrigger);
      if (!triggers.length) return;

      triggers.forEach((trigger) => {
        const parent = trigger.closest(selectors.dropdownParent);
        if (!parent) return;

        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isExpanded) {
            trigger.setAttribute('aria-expanded', 'true');
          }
        });

        parent.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
      });

      document.addEventListener('click', () => {
        this.closeAll();
      });
    },

    closeAll() {
      const triggers = document.querySelectorAll(selectors.dropdownTrigger);
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    },
  };

  /* --------------------------------------------------------------------------
     9. Smooth Scroll for Anchor Links
     -------------------------------------------------------------------------- */

  const SmoothScroll = {
    init() {
      const root = document.documentElement;

      // No sticky header → nothing is fixed at the top (base.css's fallback
      // estimate assumes a sticky header plus the bar it carries).
      if (!document.querySelector(selectors.headerSticky)) {
        root.style.setProperty('--tc-fixed-top', '0px');
      }

      // The header script publishes the fixed chrome's height (header plus
      // the offer bar it carries) as --tc-fixed-top; fall back to the header.
      const fixedTop = () => {
        const v = parseFloat(window.getComputedStyle(root).getPropertyValue('--tc-fixed-top'));
        if (v > 0) return v;
        const header = document.querySelector(selectors.header);
        return header ? header.offsetHeight : 0;
      };

      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener('click', (e) => {
          const targetId = anchor.getAttribute('href');
          if (!targetId || targetId === '#') return;

          let target = null;
          try {
            target = document.querySelector(targetId);
          } catch (err) {
            return; // not a valid selector (e.g. "#/path")
          }
          if (!target) return;

          e.preventDefault();

          const targetPosition = target.getBoundingClientRect().top + window.scrollY - fixedTop() - 12;

          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: scrollBehavior(),
          });

          // Set focus for accessibility: the next Tab continues from the
          // target. A section made focusable only for this (tabindex=-1) is
          // marked, so base.css draws no ring round the whole block.
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          if (target.getAttribute('tabindex') === '-1') target.setAttribute('data-tc-jump-target', '');
          target.focus({ preventScroll: true });
        });
      });
    },
  };

  /* --------------------------------------------------------------------------
     10. Initialise All Modules
     -------------------------------------------------------------------------- */

  function init() {
    Overlay.init();
    MobileNav.init();
    HeaderScroll.init();
    AnnouncementBar.init();
    DropdownNav.init();
    SmoothScroll.init();
  }

  // Expose for other scripts (before init, so anything that runs on
  // DOMContentLoaded after this file can already lock and trap)
  window.TheColor = window.TheColor || {};
  window.TheColor.ScrollLock = ScrollLock;
  window.TheColor.Overlay = Overlay;
  window.TheColor.MobileNav = MobileNav;
  window.TheColor.trapFocus = trapFocus;
  window.TheColor.scrollBehavior = scrollBehavior;

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
